'use client';

import { useTranslations } from 'next-intl';
import { Bike, Calendar, Car, MapPin, Users } from 'lucide-react';

import type { RegistrationVehicleType } from '../types';

export interface RegistrationSummaryCardProps {
  rideName: string;
  countryCode: string;
  stateCode: string;
  /** Already-localized event date line (resolved by the flow from config.eventDateKey) */
  eventDateLabel: string | null;
  entrant: { firstName: string; lastName: string } | null;
  vehicle: { vehicleYear?: number; vehicleMake: string; vehicleModel: string } | null;
  vehicleType: RegistrationVehicleType;
  onChangeRide: () => void;
}

/** Step-3 registration summary. Extracted from the DGR register page. */
export function RegistrationSummaryCard({
  rideName,
  countryCode,
  stateCode,
  eventDateLabel,
  entrant,
  vehicle,
  vehicleType,
  onChangeRide,
}: RegistrationSummaryCardProps) {
  const t = useTranslations('register');
  const VehicleIcon = vehicleType === 'car' ? Car : Bike;

  return (
    <div className="mb-8 rounded-xl border border-border bg-card p-5">
      <h3 className="mb-4 text-sm font-medium text-muted-foreground">{t('registrationSummary')}</h3>
      <div className="space-y-3">
        {/* Ride — tappable to change */}
        <button
          type="button"
          onClick={onChangeRide}
          className="group -mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted/60"
        >
          <MapPin className="h-5 w-5 shrink-0 text-primary" />
          <span className="flex-1 text-foreground">
            {rideName}
            {(countryCode || stateCode) && (
              <span className="ml-1 text-muted-foreground">
                ({stateCode ? `${stateCode}, ` : ''}
                {countryCode})
              </span>
            )}
          </span>
          {/* Always visible: on touch devices there is no hover to reveal it, so the
              affordance must be discoverable without it. */}
          <span className="shrink-0 text-xs font-medium text-primary">{t('changeYourRide')}</span>
        </button>
        {eventDateLabel && (
          <div className="flex items-center gap-3 text-foreground">
            <Calendar className="h-5 w-5 text-primary" />
            <span>{eventDateLabel}</span>
          </div>
        )}
        {entrant && (
          <div className="flex items-center gap-3 text-foreground">
            <Users className="h-5 w-5 text-primary" />
            <span>
              {entrant.firstName} {entrant.lastName}
            </span>
          </div>
        )}
        {vehicle && (
          <div className="flex items-center gap-3 text-foreground">
            <VehicleIcon className="h-5 w-5 text-primary" />
            <span>
              {vehicle.vehicleYear} {vehicle.vehicleMake} {vehicle.vehicleModel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
